import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";

import { StatusOptionType, PollType, PollStatusType } from "./type";
import { getLoadingOption, getPollInterval } from "./utils";

type SuccessFuncType<R> = (value: R) => void;

type PollerOptions<D, R> = {
  interval?: number;
  onChange: SuccessFuncType<R>;
  onError?: (e: unknown) => void;
  onLoadingChange?: (
    option: StatusOptionType,
    timeFrame: TimeFrameType | null,
    selfManaged?: boolean
  ) => void;
  onMissPoll?: (value: any) => void;
  formatData?: (value: D) => R;
};

export class Poller<D = any, T = D> {
  private pollIntervalMs: number;
  private taskFunc?: (arg?: any) => Promise<D>;
  private onSuccess: SuccessFuncType<T> | null;
  private onError?: (e: unknown) => void;
  private selfManaged: boolean;
  private pollInterval: NodeJS.Timeout | null;
  private skipCycle: boolean;
  private isLoading: boolean;
  private onLoadingChange?: (
    option: StatusOptionType,
    timeFrame: TimeFrameType | null,
    selfManaged?: boolean
  ) => void;
  private updateRequired?: boolean;
  private initialRunPending: boolean = true;
  private formatData?: (value: D) => T;
  private timeFrame: TimeFrameType | null = null;

  constructor({
    interval = 0,
    onChange,
    onError,
    onLoadingChange,
    formatData
  }: PollerOptions<D, T>) {
    this.pollIntervalMs = interval;
    this.onSuccess = onChange;
    this.onError = onError;
    this.selfManaged = false;
    this.pollInterval = null;
    this.isLoading = false;
    this.onLoadingChange = onLoadingChange;
    this.skipCycle = false;
    this.formatData = formatData;
  }

  pause() {
    // important to unsubscribe only if the poller is running
    // this condition helps to avoid pausing a poller that is managed
    // by a PollerGroup
    if (this.selfManaged) this.unsubscribe();
  }

  resume() {
    if (this.selfManaged && this.taskFunc && this.timeFrame?.id) {
      this.startPolling(this.taskFunc, this.timeFrame, "polling");
    }
  }

  private async startCycle(option: PollStatusType) {
    if (this.skipCycle) {
      this.updateRequired = true;
      return;
    }

    if (this.selfManaged && this.taskFunc && this.onSuccess) {
      try {
        this.updateRequired = false;
        this.setIsLoading(option);
        const value = await this.taskFunc(this.timeFrame);

        const formatData = this.formatData;
        const result = formatData ? formatData(value) : (value as unknown as T);
        this.onSuccess(result);
        this.initialRunPending = false;
      } catch (error) {
        this.onError?.(error);
      } finally {
        this.setLoadingStop(option);
      }
    }
  }

  startPolling(
    asyncFn: (timeFrame: TimeFrameType) => Promise<D>,
    timeFrame: TimeFrameType,
    type: PollType = "refresh",
    interval?: number
  ) {
    this.timeFrame = timeFrame;
    this.unsubscribe();
    this.taskFunc = asyncFn;
    this.selfManaged = true;
    this.pollIntervalMs = interval || getPollInterval(timeFrame).milliseconds;
    this.startCycle({ type, isLoading: true });

    this.pollInterval = setInterval(() => {
      this.startCycle({ type: "polling", isLoading: true });
    }, this.pollIntervalMs);
  }

  async pollOnce(
    asyncFn: (timeFrame: TimeFrameType | null) => Promise<D>,
    timeFrame: TimeFrameType | null = null,
    type: PollType = "refresh"
  ) {
    this.timeFrame = timeFrame;
    if (this.skipCycle) {
      this.updateRequired = true;
      return;
    }
    let result;

    if (this.onSuccess) {
      this.unsubscribe();
      this.setIsLoading({ type, isLoading: true });
      try {
        this.updateRequired = false;
        const value = await asyncFn(this.timeFrame);

        const formatData = this.formatData;
        result = formatData ? formatData(value) : (value as unknown as T);
        this.onSuccess(result);
        this.initialRunPending = false;
      } catch (error) {
        this.onError?.(error);
        result = error;
      } finally {
        this.setLoadingStop({ type, isLoading: false });
      }
    }
    return result;
  }

  executeWith = async (arg: TimeFrameType, type: PollType = "refresh") => {
    if (this.taskFunc !== undefined) {
      const fn = () => this.taskFunc!(arg);
      return await this.pollOnce(fn, arg, type);
    }
  };

  setTask(taskFunc: (p: any) => Promise<D>) {
    this.taskFunc = taskFunc;
  }

  unsubscribe() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.updateRequired = true;
    this.initialRunPending = true;
    this.skipCycle = false;
  }

  skipCycles(skipped: boolean) {
    this.skipCycle = skipped;
  }

  requiresRefresh() {
    return this.updateRequired !== false && !this.isLoading;
  }

  hasRunEver() {
    return this.initialRunPending;
  }

  private setIsLoading(option: PollStatusType) {
    this.isLoading = true;
    this.onLoadingChange?.(
      getLoadingOption({ ...option, isLoading: true }),
      this.timeFrame,
      this.selfManaged
    );
  }

  setLoadingStop(option: PollStatusType) {
    this.isLoading = false;
    this.onLoadingChange?.(
      getLoadingOption({ ...option, isLoading: false }),
      this.timeFrame,
      this.selfManaged
    );
  }
}
